import { Container } from '@mui/material';
import Button from '@mui/material/Button/Button';
import FormControl from '@mui/material/FormControl/FormControl';
import Grid from '@mui/material/Grid/Grid';
import TextField from '@mui/material/TextField/TextField';
import Typography from '@mui/material/Typography/Typography';
import { AreaServiceRequest, MServices, CodeMaster, CompanyBranch } from 'API';
import React, { FC } from 'react';
import GenericTemplate from '../../../template/GenericTemplate';
import { ErrorData } from './AreaServiceSelectErrorData';
import FormHelperText from '@mui/material/FormHelperText';
import { SelectValue } from 'types/SelectValue';
import SelectFormControl from 'views/parts/SelectFormControl';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DesktopDatePicker from '@mui/lab/DatePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import jaLocale from 'date-fns/locale/ja';
import BackToTopButton from 'views/parts/BackToTop';

const styles = {
  searchField: {
    '&.MuiContainer-root': {
      '@media screen and (min-width: 1200px)': {
        maxWidth: '1400px',
      },
    },
    paddingLeft: '100px',
  },

  label: {
    margin: 'auto',
    textAlign: 'right',
    pr: '10px',
    mt: '7px',
    mr: '10px',
  },

  radioLabel: {
    textAlign: 'right',
    pr: '10px',
    mt: '7px',
    mr: '10px',
  },

  categoryTitle: {
    pl: '10px',
    borderLeft: '5px solid #0058a0',
  },

  button: {
    minWidth: '120px',
    fontWeight: 'bold',
    mt: 3,
    mr: 1,
    float: 'right',
  },

  dataBlock: {
    '& > div': {
      width: '100%',
    },
  },

  categoryBlock: {
    mt: '5px',
    px: '10px',
    mb: '5px',
  },

  textField: {
    width: '100%',
    '& input': {
      padding: '8px',
    },
  },

  inputItem: {
    ph: '2px',
    boxSizing: 'border-box',
    width: '100%',
    '& > div > input': {
      maxHeight: '10px',
    },
  },
};

export interface Props {
  areaServiceData: AreaServiceRequest;
  isUpdate: boolean;
  errorAttribute: ErrorData;
  companyList: CompanyBranch[];
  serviceCategoryList: CodeMaster[];
  serviceList: MServices[];
  companyId: string;
  serviceCategoryCode: string;
  handleChangeDetail: (_: React.ChangeEvent<HTMLInputElement>) => void;
  handleClickRegist: () => void;
  handleChangeCompany: (_: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeFromDate: (_: Date) => void;
  handleChangeToDate: (_: Date) => void;
  handleChangeServiceCategory: (_: React.ChangeEvent<HTMLInputElement>) => void;
}

const AreaManagerSelect: FC<Props> = (props) => {
  const pushback = function () {
    window.history.back();
  };

  return (
    <GenericTemplate>
      <div>
        <BackToTopButton />
      </div>
      <Container sx={styles.searchField}>
        {props.errorAttribute.Header.ErrorMessage && (
          <Grid container spacing={2} sx={{ pl: 0, pb: 2 }}>
            <Grid item xs={12}>
              <FormControl component="fieldset" error={props.errorAttribute.Header.IsError}>
                <FormHelperText sx={{ ml: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>
                  {props.errorAttribute.Header.ErrorMessage}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        )}
        <Typography variant="h6" gutterBottom component="div" sx={styles.categoryTitle}>
          サービス
        </Typography>
        <Grid container spacing={0.5} sx={styles.categoryBlock}>
          <Grid container spacing={0.5} sx={styles.categoryBlock}>
            <Grid item xs={3}>
              <Typography sx={styles.label}>事業者</Typography>
            </Grid>
            <Grid item xs={9}>
              <SelectFormControl
                value={props.companyId ?? ''}
                selectName="CompanyId"
                selectValues={props.companyList
                  .filter((x) => x.CategoryCode === '01' || x.CategoryCode === '02')
                  .map((company, idx) => {
                    return { value: company.CompanyId, displayName: company.CompanyName } as SelectValue;
                  })}
                setCondition={props.handleChangeCompany}
                sx={styles.inputItem}
                isDisabled={props.isUpdate}
              />
            </Grid>
          </Grid>

          <Grid container spacing={0.5} sx={styles.categoryBlock}>
            <Grid item xs={3}>
              <Typography sx={styles.label}>サービスカテゴリ</Typography>
            </Grid>
            <Grid item xs={9}>
              <SelectFormControl
                value={props.serviceCategoryCode ?? ''}
                selectName="ServiceCategoryCode"
                selectValues={props.serviceCategoryList.map((serviceCategory, idx) => {
                  return { value: serviceCategory.Code, displayName: serviceCategory.Name } as SelectValue;
                })}
                setCondition={props.handleChangeServiceCategory}
                sx={styles.inputItem}
                isDisabled={props.isUpdate}
              />
            </Grid>
          </Grid>

          <Grid container spacing={0.5} sx={styles.categoryBlock}>
            <Grid item xs={3}>
              <Typography sx={styles.label}>サービス</Typography>
            </Grid>
            <Grid item xs={9}>
              <SelectFormControl
                value={props.areaServiceData.ServiceId ?? ''}
                selectName="ServiceId"
                selectValues={props.serviceList.map((service, idx) => {
                  return { value: service.ServiceId, displayName: service.ServiceName } as SelectValue;
                })}
                setCondition={props.handleChangeDetail}
                sx={styles.inputItem}
                isDisabled={props.isUpdate}
                error={props.errorAttribute.ServiceId.IsError}
                errorMessage={props.errorAttribute.ServiceId.ErrorMessage}
              />
            </Grid>
          </Grid>

          <Grid container spacing={0.5} sx={styles.categoryBlock}>
            <Grid item xs={3}>
              <Typography sx={styles.label}>開始日</Typography>
            </Grid>
            <Grid item xs={9}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={jaLocale}>
                <DesktopDatePicker
                  label="yyyy/mm/dd"
                  mask="____/__/__"
                  value={new Date(props.areaServiceData.FromDate ?? '')}
                  onChange={(newValue) => props.handleChangeFromDate(new Date(newValue ?? ''))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      style={{ width: '100%' }}
                      error={props.errorAttribute.FromDate.IsError}
                      helperText={props.errorAttribute.FromDate.ErrorMessage}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Grid container spacing={0.5} sx={styles.categoryBlock}>
            <Grid item xs={3}>
              <Typography sx={styles.label}>終了日</Typography>
            </Grid>
            <Grid item xs={9}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={jaLocale}>
                <DesktopDatePicker
                  label="yyyy/mm/dd"
                  mask="____/__/__"
                  value={new Date(props.areaServiceData.ToDate ?? '')}
                  onChange={(newValue) => props.handleChangeToDate(new Date(newValue ?? ''))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      style={{ width: '100%' }}
                      error={props.errorAttribute.ToDate.IsError}
                      helperText={props.errorAttribute.ToDate.ErrorMessage}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>{' '}
          </Grid>
        </Grid>
        <Button variant="contained" sx={styles.button} onClick={pushback}>
          閉じる
        </Button>
        <Button variant="contained" sx={styles.button} onClick={props.handleClickRegist}>
          登録
        </Button>
      </Container>
    </GenericTemplate>
  );
};
export default AreaManagerSelect;
